<template>
  <div :class="['business-initial-setup', { 'business-initial-setup--fullscreen': fullscreen }]">
    <v-card class="setup-shell" flat>
      <v-card-title class="setup-header">
        <div class="setup-header__content">
          <div>
            <div class="setup-kicker">Configuraciones</div>
            <h2>Configuración inicial de la sucursal</h2>
            <v-chip
              v-if="processingSeconds > 0 || authValidation.status !== 'idle'"
              small
              class="setup-timer-chip mt-2"
              color="rgba(255,255,255,0.14)"
              text-color="white"
              label
            >
              Procesando: {{ processingSecondsDisplay }}s
            </v-chip>
          </div>
          <div class="setup-header__actions">
            <v-btn
              icon
              class="setup-action"
              :title="fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'"
              @click="toggleFullscreen"
            >
              <v-icon>{{ fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="setup-body">
        <v-row>
          <v-col cols="12" lg="3" class="setup-sidebar">
            <div class="summary-card">
              <div class="summary-label">Negocio</div>
              <div class="summary-value">{{ business?.BusinessName || "Cargando..." }}</div>
              <div class="summary-note">
                El certificado se comparte entre todas las sucursales del negocio en el entorno seleccionado.
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <v-stepper v-model="step" vertical class="setup-stepper-rail">
              <v-stepper-step
                :complete="step > 1"
                step="1"
                editable
                @click.native="goToStep(1)"
              >
                Configuración facturacion electrónica
              </v-stepper-step>
              <v-stepper-step
                :complete="step > 2"
                step="2"
                editable
                @click.native="goToStep(2)"
              >
                Configurar Sucursal
              </v-stepper-step>
              <v-stepper-step
                :complete="step >= 3"
                step="3"
                :editable="isTestEnvironment"
                @click.native="goToStep(3)"
              >
                Pruebas de facturacion electronica
              </v-stepper-step>
            </v-stepper>
          </v-col>

          <v-col cols="12" lg="9">
            <v-stepper v-model="step" class="setup-stepper">
              <v-stepper-items>
                <v-stepper-content step="1" style="background: #FFF;">
                    <v-card class="setup-panel setup-panel--white" flat>
                    <div class="panel-title">1. Configuración MH completa</div>
                    <div class="panel-text">
                      Todo el flujo de Ministerio de Hacienda queda agrupado en un solo paso. Seleccione la sucursal, confirme el NIT, elija el entorno, configure las contraseñas, suba el certificado y valide la autenticación.
                    </div>

                    <v-row>
                      <v-col cols="12" md="8">
                        <v-select
                          v-model="selectedBranchId"
                          :items="branchOptions"
                          item-text="BranchName"
                          item-value="ID_Branch"
                          label="Sucursal"
                          outlined
                          dense
                          @change="requestBranchChange"
                        />
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model="nitValue"
                          label="NIT / DUI del negocio"
                          :name="`business_nit_${win?.uuid || 'setup'}`"
                          autocomplete="off"
                          autocapitalize="off"
                          autocorrect="off"
                          spellcheck="false"
                          :readonly="nitAutofillGuard"
                          @focus="nitAutofillGuard = false"
                          @click="nitAutofillGuard = false"
                          outlined
                          dense
                          :rules="[nitRule]"
                          @input="nitTouched = true"
                        />
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" md="6">
                        <v-radio-group v-model="environment" row @change="handleEnvironmentChange">
                          <v-radio label="Pruebas" value="00" />
                          <v-radio label="Producción" value="01" />
                        </v-radio-group>
                      </v-col>
                      <v-col cols="12" md="6">
                        <div class="firmador-url-control">
                          <v-text-field
                            ref="urlfirm"
                            v-model="authfedata.UrlFirmador"
                            label="URL Firmador"
                            outlined
                            dense
                            :readonly="!firmadorUrlEditable"
                          />
                          <v-btn
                            class="firmador-url-control__button"
                            icon
                            small
                            color="primary"
                            :title="firmadorUrlEditable ? 'Bloquear URL' : 'Editar URL'"
                            :aria-label="firmadorUrlEditable ? 'Bloquear URL' : 'Editar URL'"
                            @click="toggleFirmadorUrlEditMode"
                          >
                            <v-icon small>
                              {{ firmadorUrlEditable ? "mdi-lock" : "mdi-pencil" }}
                            </v-icon>
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>

                    <div class="password-section">
                      <div class="panel-subtitle">
                        <div class="password-actions password-actions--top float-end">
                          <v-btn small color="info" outlined @click="openCopyPasswordsDialog">
                            Copiar de otra sucursal
                          </v-btn>
                        </div>
                        Configuración de contraseñas
                      </div>
                      <div class="row mt-4">
                        <v-col cols="12" lg="6">
                          <v-card class="password-card" outlined>
                            <v-card-title class="password-card__title-row">
                              <span>Contraseña de autenticación</span>
                              <v-spacer />
                              <v-btn icon small @click="showAuthPasswordInfo = !showAuthPasswordInfo" :aria-label="showAuthPasswordInfo ? 'Ocultar ayuda' : 'Mostrar ayuda'">
                                <v-icon small>mdi-information-outline</v-icon>
                              </v-btn>
                            </v-card-title>
                            <v-card-text>
                              <v-expand-transition>
                                <v-alert v-if="showAuthPasswordInfo" type="info" outlined border="left" dense class="mb-4">
                                  Esta contraseña debe configurarse también en el portal del Ministerio de Hacienda en la sección correspondiente a autenticación del contribuyente. Genere una contraseña, cópiela y péguela en el portal antes de guardar.
                                </v-alert>
                              </v-expand-transition>
                              <v-text-field
                                v-model="authfedata.AuthFePassword"
                                label="Contraseña de autenticación"
                                outlined
                                dense
                                :append-icon="showAuthPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                @click:append="showAuthPassword = !showAuthPassword"
                                :type="showAuthPassword ? 'text' : 'password'"
                              />
                              <div class="password-actions">
                                <v-btn small color="secondary" @click="generatePassword('AuthFePassword')">Generar</v-btn>
                                <v-btn small color="primary" :disabled="!authfedata.AuthFePassword" @click="copyToClipboard(authfedata.AuthFePassword)">Copiar</v-btn>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <v-col cols="12" lg="6">
                          <v-card class="password-card" outlined>
                            <v-card-title class="password-card__title-row">
                              <span>Contraseña privada del firmador</span>
                              <v-spacer />
                              <v-btn icon small @click="showPrivatePasswordInfo = !showPrivatePasswordInfo" :aria-label="showPrivatePasswordInfo ? 'Ocultar ayuda' : 'Mostrar ayuda'">
                                <v-icon small>mdi-information-outline</v-icon>
                              </v-btn>
                            </v-card-title>
                            <v-card-text>
                              <v-expand-transition>
                                <v-alert v-if="showPrivatePasswordInfo" type="info" outlined border="left" dense class="mb-4">
                                  Esta contraseña será utilizada por el firmador para procesar documentos electrónicos. Genere una contraseña, cópiela y configúrela en el portal del Ministerio de Hacienda donde se solicita la contraseña privada del certificado o firmador.
                                </v-alert>
                              </v-expand-transition>
                              <v-text-field
                                v-model="authfedata.AuthFePasswordPri"
                                label="Contraseña privada para firmador"
                                outlined
                                dense
                                :append-icon="showPrivatePassword ? 'mdi-eye-off' : 'mdi-eye'"
                                @click:append="showPrivatePassword = !showPrivatePassword"
                                :type="showPrivatePassword ? 'text' : 'password'"
                              />
                              <div class="password-actions">
                                <v-btn small color="secondary" @click="generatePassword('AuthFePasswordPri')">Generar</v-btn>
                                <v-btn small color="primary" :disabled="!authfedata.AuthFePasswordPri" @click="copyToClipboard(authfedata.AuthFePasswordPri)">Copiar</v-btn>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </div>
                    </div>

                    <div class="panel-actions panel-actions--split">
                      <v-btn color="primary" :disabled="!canSavePasswords" :loading="savingPasswords" @click="savePasswords">
                        Guardar contraseñas
                      </v-btn>
                    </div>

                    <v-divider class="my-6"></v-divider>

                    <div class="panel-subtitle">Certificado .crt</div>
                    <div class="panel-text">
                      El archivo se guarda en DigitalOcean Spaces en la carpeta correspondiente al entorno. No se aceptan formatos distintos a .crt.
                    </div>

                    <v-alert v-if="currentCertificate" type="success" outlined border="left" dense>
                      <div class="d-flex flex-column flex-md-row justify-space-between align-md-center">
                        <div>
                          <strong>Certificado activo encontrado.</strong>
                          <div>{{ currentCertificate.original_filename }}</div>
                        </div>
                        <div class="mt-2 mt-md-0">
                          <v-btn color="error" small @click="openDeleteDialog">
                            Eliminar / reemplazar
                          </v-btn>
                        </div>
                      </div>
                    </v-alert>

                    <v-row v-else>
                      <v-col cols="12">
                        <v-file-input
                          v-model="selectedCertificateFile"
                          label="Archivo .crt"
                          accept=".crt"
                          outlined
                          dense
                          prepend-icon="mdi-certificate"
                          :rules="[certificateRule]"
                          @change="handleCertificateSelection"
                        />
                      </v-col>
                    </v-row>

                    <v-alert type="info" outlined border="left" dense>
                      El certificado aplica para todas las sucursales del negocio en el entorno seleccionado.
                    </v-alert>

                    <div class="panel-actions">
                      <v-btn
                        color="primary"
                        :disabled="!selectedCertificateFile || !nitIsValid"
                        :loading="uploadingCertificate"
                        @click="uploadCertificate"
                      >
                        {{ currentCertificate ? "Reemplazar certificado" : "Subir certificado" }}
                      </v-btn>
                    </div>

                    <div class="processing-block mt-6" v-if="processingSeconds > 0 || authValidation.status !== 'idle'">
                      <div class="panel-subtitle">Procesamiento y validación</div>
                      <div class="panel-text">
                        Procesando el certificado. El firmador puede tardar aproximadamente 1 minuto en sincronizar los cambios. Espere a que finalice el contador antes de validar la autenticación.
                      </div>

                      <div class="processing-box">
                        <v-progress-circular :size="90" :width="8" color="primary" indeterminate v-if="processingSeconds > 0" />
                        <div v-else class="processing-box__done">
                          <v-icon large color="success">mdi-check-circle</v-icon>
                        </div>

                        <div class="processing-box__timer">
                          <div class="timer-value">{{ processingSecondsDisplay }}</div>
                          <div class="timer-label">segundos restantes</div>
                        </div>
                      </div>

                      <v-progress-linear :value="processingProgress" height="10" rounded color="primary" class="mt-4" />

                      <div class="panel-actions">
                        <v-btn text :disabled="processingSeconds > 0 || authValidation.status === 'success'" @click="refreshCurrentCertificate">
                          Actualizar estado
                        </v-btn>
                        <v-btn color="primary" :disabled="processingSeconds > 0" :loading="validatingAuth" @click="validateAuthentication">
                          Validar autenticación
                        </v-btn>
                      </div>

                      <v-alert v-if="authValidation.status === 'success'" type="success" outlined border="left" dense>
                        {{ authValidation.message || "Configuración validada correctamente. La sucursal y el certificado del entorno seleccionado están listos para operar." }}
                      </v-alert>
                      <v-alert v-else-if="authValidation.status === 'error'" type="error" outlined border="left" dense>
                        {{ authValidation.message || defaultValidationMessage }}
                      </v-alert>
                      <v-alert v-else type="info" outlined border="left" dense>
                        {{ defaultValidationMessage }}
                      </v-alert>
                    </div>

                    <div class="panel-actions panel-actions--split">
                     
                      <v-btn color="primary" @click="step = 2">
                        Continuar
                      </v-btn>
                    </div>
                  </v-card>
                </v-stepper-content>

                <v-stepper-content step="2" style="background-color: #FFF;">
                  <v-card class="setup-panel setup-panel--white" flat>
                    <div class="panel-title">2. Configuración de sucursal</div>
                    <div class="panel-text">
                      Reutilice la lógica existente de sucursales para cargar la imagen y completar los mismos campos visibles en el formulario de sucursal.
                    </div>

                    <v-row>
                      <v-col cols="12" lg="4">
                        <div class="branch-logo-box">
                          <v-img
                            :src="branchLogoPreview || branchForm.BranchLogoPath || null"
                            class="preview-avatar"
                            contain
                          />
                        </div>
                        <v-file-input
                          v-model="branchLogoFile"
                          label="Imagen factura"
                          accept="image/*"
                          outlined
                          dense
                          prepend-icon="mdi-camera"
                          @change="handleBranchLogoSelection"
                        />

                        <v-card class="setup-summary-card mt-4" outlined>
                          <v-card-text>
                            <div class="summary-label">Sucursal activa</div>
                            <div class="summary-value">{{ selectedBranch?.BranchName || branchForm.BranchName || "-" }}</div>
                            <div class="summary-note">
                              Los cambios de este paso se guardan en la sucursal seleccionada.
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>

                      <v-col cols="12" lg="8">
                        <v-row>
                          <v-col cols="12">
                            <div class="panel-subtitle">Datos principales</div>
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="branchForm.BranchName"
                              label="Nombre sucursal*"
                              outlined
                              dense
                              :rules="[requiredRule]"
                              required
                              placeholder="Digite el nombre de la sucursal"
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="branchForm.BranchPhone"
                              label="Télefono sucursal*"
                              outlined
                              dense
                              :rules="[requiredRule]"
                              required
                              placeholder="Digite el télefono de la sucursal"
                              @input="maskNum"
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="branchForm.BranchEmail"
                              label="Correo electrónico sucursal*"
                              outlined
                              dense
                              :rules="[requiredRule]"
                              required
                              placeholder="Digite el correo de la sucursal"
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field
                              v-model="branchForm.BranchMobile"
                              label="Celular sucursal"
                              outlined
                              dense
                              placeholder="Digite el télefono celular de la sucursal"
                              @input="maskNum"
                            />
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="branchForm.BranchAddress"
                              label="Dirección sucursal*"
                              outlined
                              dense
                              :rules="[requiredRule]"
                              required
                              placeholder="Digite la dirección de la sucursal"
                            />
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="branchForm.currency"
                              :items="getcurrency"
                              item-text="name"
                              item-value="value"
                              label="Moneda*"
                              outlined
                              dense
                              :rules="[requiredRule]"
                              required
                              attach
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="my-2" />
                          </v-col>

                          <v-col cols="12">
                            <div class="panel-subtitle">Responsables</div>
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchManagerName" label="Nombre gerente" outlined dense placeholder="Digite el nombre de gerente de la sucursal" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchManagerPhone" label="Télefono gerente" outlined dense placeholder="Digite el télefono del gerente de la sucursal" @input="maskNum" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchManagerMobile" label="Celular gerente" outlined dense placeholder="Digite el télefono celular del gerente" @input="maskNum" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchManagerEmail" label="Correo eléctronico gerente" outlined dense placeholder="Digite el correo eléctronico del gerente" />
                          </v-col>
                          <v-col cols="12" md="12" class="py-0">
                            <v-checkbox
                              v-model="useSameManagerContactInfo"
                              class="mt-0 pt-0 mb-4"
                              hide-details
                              label="Utilizar misma información en gerente y contacto"
                              @change="toggleSameManagerContactInfo"
                            />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchContactName" :disabled="useSameManagerContactInfo" label="Nombre contacto" outlined dense placeholder="Digite un nombre de contacto para la sucursal" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchContactPhone" :disabled="useSameManagerContactInfo" label="Télefono contacto" outlined dense placeholder="Digite un télefono de contacto para la sucursal" @input="maskNum" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchContactMobile" :disabled="useSameManagerContactInfo" label="Celular contacto" outlined dense placeholder="Digite un télefono celular de contacto para la sucursal" @input="maskNum" />
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-text-field v-model="branchForm.BranchContactEmail" :disabled="useSameManagerContactInfo" label="Correo eléctronico contacto" outlined dense placeholder="Digite un correo eléctronico de contacto para la sucursal" />
                          </v-col>

                          <v-col cols="12">
                            <v-divider class="my-2" />
                          </v-col>

                          <v-col cols="12">
                            <div class="panel-subtitle">Facturación y operación</div>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="branchForm.BranchLegalName"
                              label="Nombre legal de la sucursal*"
                              outlined
                              dense
                              :rules="[requiredRule]"
                              required
                              placeholder="Digite el nombre legal de la sucursal"
                            />
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-textarea
                              v-model="branchForm.BranchLegalAddress"
                              label="Dirección legal de la sucursal*"
                              outlined
                              dense
                              rows="2"
                              :rules="[requiredRule]"
                              required
                              placeholder="Digite la dirección legal de la sucursal"
                            />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-autocomplete
                              v-model="branchForm.country"
                              :items="countryList"
                              item-text="name"
                              item-value="name"
                              label="País*"
                              outlined
                              dense
                              attach
                              required
                              @change="onCountryChange"
                            />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-autocomplete
                              v-if="checkcountry"
                              v-model="branchForm.branchdepartment"
                              :items="departments"
                              item-text="Valores"
                              item-value="Codigo"
                              label="Estado/Departamento"
                              outlined
                              dense
                              attach
                            />
                            <v-autocomplete
                              v-else
                              v-model="branchForm.branchdepartment"
                              :items="stateList"
                              item-text="name"
                              item-value="name"
                              label="Estado/Departamento"
                              outlined
                              dense
                              attach
                              @change="onDepartmentChange"
                            >
                              <template v-slot:no-data>
                                <div class="px-4">{{ noDataLabelStates }}</div>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-autocomplete
                              v-if="checkcountry"
                              v-model="branchForm.branchmunicipality"
                              :items="municipalities"
                              item-text="Valores"
                              item-value="Codigo"
                              label="Ciudad"
                              outlined
                              dense
                              attach
                            />
                            <v-autocomplete
                              v-else
                              v-model="branchForm.branchmunicipality"
                              :items="cityList"
                              item-text="name"
                              item-value="name"
                              label="Ciudad"
                              outlined
                              dense
                              attach
                              :readonly="!branchForm.branchdepartment"
                            >
                              <template v-slot:no-data>
                                <div class="px-4">{{ noDataLabelCities }}</div>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-autocomplete
                              v-model="branchForm.establishmenttype"
                              :items="establisment"
                              item-text="Valores"
                              item-value="Codigo"
                              label="Tipo establecimiento*"
                              outlined
                              dense
                              attach
                              required
                            />
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="branchForm.codestablemh"
                              :maxlength="4"
                              label="Codigo establecimiento MH*"
                              outlined
                              dense
                              :rules="[requiredRule]"
                              required
                            />
                          </v-col>
                          <v-col cols="12" md="8">
                            <v-combobox
                              v-model="branchForm.BccEmails"
                              :items="branchForm.BccEmails"
                              multiple
                              chips
                              outlined
                              attach
                              label="Enviar facturas a (como copias ocultas)"
                              clearable
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>

                    <div class="panel-actions">
                      <v-btn text @click="step = 1">Atrás</v-btn>
                      <v-btn
                        v-if="isTestEnvironment"
                        color="secondary"
                        outlined
                        @click="goToStep(3)"
                      >
                        Ir a pruebas
                      </v-btn>
                      <v-btn color="primary" :loading="savingBranchData" @click="saveBranchData">
                        Guardar sucursal
                      </v-btn>
                    </div>
                  </v-card>
                </v-stepper-content>

                <v-stepper-content step="3" style="background-color: #FFF;">
                  <v-card class="setup-panel setup-panel--white" flat>
                    <div class="panel-title">3. Pruebas de facturacion electronica</div>
                    <div class="panel-text">
                      Este paso solo está disponible en entorno de pruebas para validar el envío de comprobantes usando el flujo actual.
                    </div>

                    <template v-if="isTestEnvironment && selectedBranchMhEnabled">
                      <TestDteSender :business="business" :branch="selectedBranch" />
                    </template>

                    <v-alert v-else type="warning" outlined border="left" dense class="mb-0">
                      <template v-if="!isTestEnvironment">
                        Este paso está bloqueado en producción. Cambie el entorno a Pruebas para habilitar el envío de comprobantes de prueba.
                      </template>
                      <template v-else>
                        La sucursal seleccionada no tiene facturación electrónica activa. Vuelva al paso de Configuración facturación electrónica para completar la activación antes de ejecutar las pruebas.
                      </template>
                    </v-alert>

                    <div class="panel-actions">
                      <v-btn text @click="goToStep(2)">Atrás</v-btn>
                    </div>
                  </v-card>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="branchChangeDialog" max-width="560">
      <v-card>
        <v-card-title>Confirmar cambio de sucursal</v-card-title>
        <v-card-text>
          <p>
            Las contraseñas MH se guardarán en la sucursal seleccionada.
          </p>
          <p class="mb-0">
            Sucursal actual: <strong>{{ selectedBranch?.BranchName || "-" }}</strong>
          </p>
          <p>
            Nueva sucursal: <strong>{{ branchById(pendingBranchId)?.BranchName || "-" }}</strong>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelBranchChange">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmBranchChange">Aceptar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="620">
      <v-card>
        <v-card-title class="delete-dialog__title">Eliminar certificado</v-card-title>
        <v-card-text>
          <v-alert type="error" color="error" dark dense border="left" class="mb-3 delete-dialog__alert">
            Esta acción eliminará el certificado utilizado por el firmador para este negocio y entorno. Si continúa, los documentos electrónicos pueden dejar de firmarse hasta que suba un nuevo certificado. Confirme que entiende el impacto de esta acción.
          </v-alert>
          <v-checkbox
            v-model="deleteConfirmChecked"
            label="Confirmo que deseo eliminar este certificado y entiendo que puede afectar el firmador."
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDeleteDialog">Cancelar</v-btn>
          <v-btn color="error" :disabled="!deleteConfirmChecked || deletingCertificate" :loading="deletingCertificate" @click="deleteCertificate">
            Aceptar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="copyPasswordsDialog" max-width="620">
      <v-card>
        <v-card-title>Copiar contraseñas de otra sucursal</v-card-title>
        <v-card-text>
          <v-alert type="info" outlined border="left" dense class="mb-4">
            Seleccione una sucursal del mismo negocio para copiar sus contraseñas MH. Solo se copiarán la contraseña de autenticación y la contraseña privada del firmador.
          </v-alert>
          <v-select
            v-model="copyPasswordsSourceBranchId"
            :items="otherBranchOptions"
            item-text="BranchName"
            item-value="ID_Branch"
            label="Sucursal origen"
            outlined
            dense
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCopyPasswordsDialog">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!copyPasswordsSourceBranchId || copyingPasswordsFromBranch" :loading="copyingPasswordsFromBranch" @click="copyPasswordsFromBranch">
            Copiar contraseñas
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TestDteSender from "../components/TestDteSender.vue";

export default {
  name: "BusinessInitialSetup",
  components: {
    TestDteSender,
  },
  props: ["win"],
  data() {
    return {
      business: null,
      branches: [],
      selectedBranchId: null,
      confirmedBranchId: null,
      pendingBranchId: null,
      branchChangeDialog: false,
      nitValue: "",
      nitTouched: false,
      nitAutofillGuard: true,
      environment: "00",
      step: 1,
      firmadorUrlEditable: false,
      authfedata: {
        AuthFeUser: "",
        AuthFePassword: "",
        AuthFePasswordPri: "",
        AuthFeActive: 1,
        AuthFeMode: "0",
        UrlFirmador: "",
      },
      showAuthPassword: false,
      showPrivatePassword: false,
      showAuthPasswordInfo: false,
      showPrivatePasswordInfo: false,
      savingPasswords: false,
      selectedCertificateFile: null,
      currentCertificate: null,
      uploadingCertificate: false,
      deletingCertificate: false,
      deleteDialog: false,
      deleteConfirmChecked: false,
      copyPasswordsDialog: false,
      copyPasswordsSourceBranchId: null,
      copyingPasswordsFromBranch: false,
      processingSeconds: 0,
      processingTimer: null,
      validatingAuth: false,
      authValidation: {
        status: "idle",
        message: "",
      },
      loadedBranchAuth: false,
      loadingState: false,
      fullscreen: false,
      showDrawerBeforeFullscreen: null,
      useSameManagerContactInfo: false,
      branchForm: {
        ID_Branch: null,
        ID_Business: null,
        BranchName: "",
        BranchPhone: "",
        BranchMobile: "",
        BranchEmail: "",
        BranchAddress: "",
        BranchManagerName: "",
        BranchManagerPhone: "",
        BranchManagerMobile: "",
        BranchManagerEmail: "",
        BranchContactName: "",
        BranchContactPhone: "",
        BranchContactMobile: "",
        BranchContactEmail: "",
        BranchLegalName: "",
        BranchLegalNRC: "",
        BranchLegalNIT: "",
        BranchLegalAddress: "",
        BranchLogoPath: "",
        currency: "",
        country: "",
        branchdepartment: "",
        branchmunicipality: "",
        establishmenttype: "",
        codestable: "",
        codestablemh: "",
        codpuntoventa: "",
        codpuntoventamh: "",
        BccEmails: [],
        BranchBilledInfo: "",
        BranchPrincipalPriceList: {
          edited: false,
          value: null,
        },
        UseDteTemplateV2: 0,
        DteTemplateDefaultLayoutKey: "",
      },
      branchLogoFile: null,
      branchLogoPreview: null,
      savingBranchData: false,
      stateList: [],
      cityList: [],
      noDataLabelStates: "Seleccione un país",
      noDataLabelCities: "No hay ciudades disponibles",
    };
  },
  computed: {
    currentUser() {
      return this.$store?.state?.user || {};
    },
    currentBranchStore() {
      return this.$store?.state?.CurrentBranch || {};
    },
    isEmbeddedInBusinessTabs() {
      return this.win?.component === "Business";
    },
    businessId() {
      return this.resolveBusinessId();
    },
    businessBillingNit() {
      return this.business?.billingnit || this.business?.BusinessBillingNIT || this.win?.meta?.business?.billingnit || this.win?.meta?.business?.BusinessBillingNIT || "";
    },
    businessBillingNrc() {
      return this.business?.billingnrc || this.business?.BusinessBillingNRC || this.win?.meta?.business?.billingnrc || this.win?.meta?.business?.BusinessBillingNRC || "";
    },
    departments() {
      const getter = this.$store?.getters?.getCatalogMH;
      return typeof getter === "function" ? getter("Departamento") || [] : [];
    },
    municipalities() {
      const getter = this.$store?.getters?.getCatalogMH;
      if (this.branchForm?.branchdepartment == null) return [];
      const municipalities = typeof getter === "function" ? getter("Municipio") || [] : [];

      if (this.branchForm?.branchdepartment) {
        return municipalities.filter((mun) => mun.Departamento == this.branchForm.branchdepartment);
      }
      return [];
    },
    establisment() {
      const getter = this.$store?.getters?.getCatalogMH;
      return typeof getter === "function" ? getter("TipoEstablecimiento") || [] : [];
    },
    countryList() {
      return this.$store?.getters?.getCountries || [];
    },
    checkcountry() {
      return this.branchForm?.country === "El Salvador";
    },
    getcurrency() {
      return (this.countryList || []).map((country) => ({
        name: country.currency_name,
        value: country.currency_symbol,
        currentcountry: country.name,
      }));
    },
    selectedBranch() {
      return this.branchById(this.selectedBranchId);
    },
    selectedBranchMhEnabled() {
      return Number(this.authfedata?.AuthFeActive ?? this.selectedBranch?.AuthFeActive ?? 0) === 1;
    },
    branchOptions() {
      return this.branches || [];
    },
    otherBranchOptions() {
      return (this.branches || []).filter((branch) => String(branch.ID_Branch) !== String(this.selectedBranchId));
    },
    nitIsValid() {
      const nit = String(this.nitValue || "").replace(/-/g, "");
      return nit.length === 9 || nit.length === 14;
    },
    environmentLabel() {
      return this.environment === "01" ? "Producción" : "Pruebas";
    },
    isTestEnvironment() {
      return this.environment === "00";
    },
    currentFolder() {
      return this.environment === "01"
        ? (process.env.VUE_APP_MH_CERTIFICATES_PRODUCTION_FOLDER || "certificado_produccion")
        : (process.env.VUE_APP_MH_CERTIFICATES_TEST_FOLDER || "certificado_test");
    },
    presetFirmadorUrl() {
      return this.getFirmadorUrlForEnvironment(this.environment);
    },
    firmadorUrl() {
      return this.authfedata?.UrlFirmador || this.presetFirmadorUrl;
    },
    canSavePasswords() {
      return !!this.selectedBranchId && this.nitIsValid && !!this.environment && !!this.authfedata.AuthFePassword && !!this.authfedata.AuthFePasswordPri;
    },
    processingProgress() {
      const total = 60;
      return Math.max(0, Math.min(100, ((total - this.processingSeconds) / total) * 100));
    },
    processingSecondsDisplay() {
      return String(this.processingSeconds).padStart(2, "0");
    },
    defaultValidationMessage() {
      return "La validación aún no se ha ejecutado.";
    },
  },
  async mounted() {
    await this.initialize();
  },
  watch: {
    async step(newValue, oldValue) {
      if (String(newValue) === "2" && String(oldValue) !== "2") {
        await this.refreshBranchSetupForm();
      }
    },
    "branchForm.BranchManagerName"() {
      if (this.useSameManagerContactInfo) this.syncManagerContactInfo();
    },
    "branchForm.BranchManagerPhone"() {
      if (this.useSameManagerContactInfo) this.syncManagerContactInfo();
    },
    "branchForm.BranchManagerMobile"() {
      if (this.useSameManagerContactInfo) this.syncManagerContactInfo();
    },
    "branchForm.BranchManagerEmail"() {
      if (this.useSameManagerContactInfo) this.syncManagerContactInfo();
    },
  },
  beforeDestroy() {
    this.clearProcessingTimer();
    if (this.branchLogoPreview && this.branchLogoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(this.branchLogoPreview);
    }
    if (this.showDrawerBeforeFullscreen !== null) {
      this.$store.commit("setShowDrawer", this.showDrawerBeforeFullscreen);
      this.showDrawerBeforeFullscreen = null;
    }
    this.restoreBodyOverflow();
  },
  methods: {
    branchById(id) {
      return this.branches.find((branch) => String(branch.ID_Branch) === String(id)) || null;
    },
    nitRule(value) {
      const nit = String(value || "").replace(/-/g, "");
      return nit.length === 9 || nit.length === 14 || "El NIT no tiene la longitud requerida.";
    },
    isValidNitCandidate(value) {
      const nit = String(value || "").replace(/-/g, "");
      return nit.length === 9 || nit.length === 14;
    },
    getBusinessNit() {
      return (
        this.businessBillingNit ||
        ""
      );
    },
    certificateRule(value) {
      if (!value) return true;
      const filename = value?.name || value?.originalname || "";
      return String(filename).toLowerCase().endsWith(".crt") || "Solo se aceptan archivos .crt.";
    },
    async initialize() {
      this.loadingState = true;
      try {
        await this.loadBusiness();
        await this.loadBranches();
        await this.loadBranchAuthInfo();
        await this.ensureSelectedBranchDefaults();
        this.nitAutofillGuard = true;
        await this.refreshCurrentCertificate();
        this.$nextTick(() => {
          this.nitAutofillGuard = true;
        });
      } finally {
        this.loadingState = false;
      }
    },
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen;
      if (this.fullscreen && this.showDrawerBeforeFullscreen === null) {
        this.showDrawerBeforeFullscreen = this.$store.state.showDrawer;
      }
      this.$store.commit("setShowDrawer", !this.fullscreen);
      this.syncBodyOverflow();
      if (!this.fullscreen && this.showDrawerBeforeFullscreen !== null) {
        this.$store.commit("setShowDrawer", this.showDrawerBeforeFullscreen);
        this.showDrawerBeforeFullscreen = null;
      }
    },
    handleCloseRequest() {
      if (this.isEmbeddedInBusinessTabs) {
        this.$emit("close-request");
        return;
      }

      this.$store.dispatch("deleteWindowByUuid", { uuid: this.win.uuid });
    },
    syncBodyOverflow() {
      if (typeof document === "undefined") return;
      document.body.style.overflow = this.fullscreen ? "hidden" : "";
    },
    restoreBodyOverflow() {
      if (typeof document === "undefined") return;
      document.body.style.overflow = "";
    },
    goToStep(stepNumber) {
      const nextStep = String(stepNumber);
      if (this.step === nextStep) return;

      if (nextStep === "3" && this.isTestEnvironment && !this.selectedBranchMhEnabled) {
        this.showMessage(
          "warning",
          "Configuración pendiente",
          "La sucursal seleccionada no tiene facturación electrónica activa. Vuelva al paso de Configuración facturación electrónica para completarla antes de ejecutar las pruebas."
        );
        this.step = "1";
        return;
      }

      if (nextStep === "3" && !this.isTestEnvironment) {
        this.showMessage(
          "warning",
          "Paso bloqueado",
          "Las pruebas de facturación electrónica solo están disponibles en entorno de pruebas."
        );
        this.step = "2";
        return;
      }

      this.step = nextStep;
    },
    async loadBusiness() {
      const businessId = this.resolveBusinessId();
      if (!businessId) {
        this.business = this.win?.meta?.business || null;
        return;
      }

      try {
        const freshBusiness = await this.$API.business.FindOne(businessId);
        this.business = freshBusiness || this.win?.meta?.business || null;
        if (this.win?.meta) {
          this.$set(this.win.meta, "business", this.business);
        }
      } catch (error) {
        this.business = this.win?.meta?.business || null;
      }
    },
    async loadBranches(options = {}) {
      const preserveSelection = Boolean(options.preserveSelection);
      const businessId = this.resolveBusinessId();
      if (!businessId) return;

      let response = [];
      try {
        response = await this.$API.branches.findBranchesHistory({
          ID_Business: businessId,
        });
      } catch (error) {
        response = [];
      }

      if (!Array.isArray(response) || response.length === 0) {
        try {
          response = await this.$API.branches.findBranches(businessId);
        } catch (error) {
          response = [];
        }
      }

      this.branches = Array.isArray(response) ? response : [];

      const currentBranchId = preserveSelection && this.selectedBranchId
        ? this.selectedBranchId
        : (this.currentBranchStore?.ID_Branch || this.currentUser?.branch);
      const branchExists = this.branches.some((branch) => String(branch.ID_Branch) === String(currentBranchId));
      this.selectedBranchId = branchExists ? currentBranchId : (this.branches[0]?.ID_Branch || null);
      this.confirmedBranchId = this.selectedBranchId;
      this.syncBranchFormFromSelected();
    },
    async ensureSelectedBranchDefaults() {
      if (!this.nitTouched) {
        const authFeUser = this.isValidNitCandidate(this.authfedata.AuthFeUser) ? this.authfedata.AuthFeUser : "";
        const businessNit = this.getBusinessNit();
        this.nitValue = authFeUser || businessNit || this.nitValue;
      }

      this.syncBranchFormFromSelected();
    },
    buildBranchForm(branch = {}) {
      const bccEmails = Array.isArray(branch.BccEmails)
        ? branch.BccEmails.filter(Boolean)
        : String(branch.BccEmails || "")
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean);

      return {
        ID_Branch: branch.ID_Branch || null,
        ID_Business: branch.ID_Business || this.businessId || null,
        BranchName: branch.BranchName || "",
        BranchPhone: branch.BranchPhone || "",
        BranchMobile: branch.BranchMobile || "",
        BranchEmail: branch.BranchEmail || "",
        BranchAddress: branch.BranchAddress || "",
        BranchManagerName: branch.BranchManagerName || "",
        BranchManagerPhone: branch.BranchManagerPhone || "",
        BranchManagerMobile: branch.BranchManagerMobile || "",
        BranchManagerEmail: branch.BranchManagerEmail || "",
        BranchContactName: branch.BranchContactName || "",
        BranchContactPhone: branch.BranchContactPhone || "",
        BranchContactMobile: branch.BranchContactMobile || "",
        BranchContactEmail: branch.BranchContactEmail || "",
        BranchLegalName: branch.BranchLegalName || "",
        BranchLegalNRC: branch.BranchLegalNRC || "",
        BranchLegalNIT: branch.BranchLegalNIT || "",
        BranchLegalAddress: branch.BranchLegalAddress || "",
        BranchLogoPath: branch.BranchLogoPath || "",
        currency: branch.currency || branch.Currency || "",
        country: branch.country || branch.Country || "",
        branchdepartment: branch.branchdepartment || branch.BranchDepartment || "",
        branchmunicipality: branch.branchmunicipality || branch.BranchMunicipality || "",
        establishmenttype: branch.establishmenttype || branch.EstablishmentType || "",
        codestable: branch.codestable || branch.CodEstable || "",
        codestablemh: branch.codestablemh || branch.CodEstableMH || "",
        codpuntoventa: branch.codpuntoventa || branch.CodPuntoVenta || "",
        codpuntoventamh: branch.codpuntoventamh || branch.CodPuntoVentaMH || "",
        BccEmails: bccEmails,
        BranchBilledInfo: branch.BranchBilledInfo || "",
        BranchPrincipalPriceList: branch.BranchPrincipalPriceList || {
          edited: false,
          value: null,
        },
        UseDteTemplateV2: branch.UseDteTemplateV2 != null ? Number(branch.UseDteTemplateV2) : 0,
        DteTemplateDefaultLayoutKey: branch.DteTemplateDefaultLayoutKey || "",
      };
    },
    syncBranchFormFromSelected() {
      const selected = this.selectedBranch || this.currentBranchStore || {};
      if (this.branchLogoPreview && this.branchLogoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.branchLogoPreview);
      }
      this.branchForm = this.buildBranchForm(selected);
      this.branchLogoPreview = selected.BranchLogoPath || null;
      this.branchLogoFile = null;
      this.useSameManagerContactInfo = this.areManagerAndContactEqual();
      this.copyBusinessBillingDefaults();
      this.refreshBranchGeoData();
    },
    async refreshBranchSetupForm() {
      if (!this.businessId) return;
      this.branchForm = this.buildBranchForm({});
      this.branchLogoFile = null;
      this.branchLogoPreview = null;
      this.useSameManagerContactInfo = false;
      this.firmadorUrlEditable = false;
      await this.loadBranches({ preserveSelection: true });
    },
    areManagerAndContactEqual() {
      const managerValues = [
        this.branchForm?.BranchManagerName || "",
        this.branchForm?.BranchManagerPhone || "",
        this.branchForm?.BranchManagerMobile || "",
        this.branchForm?.BranchManagerEmail || "",
      ];
      const contactValues = [
        this.branchForm?.BranchContactName || "",
        this.branchForm?.BranchContactPhone || "",
        this.branchForm?.BranchContactMobile || "",
        this.branchForm?.BranchContactEmail || "",
      ];
      const hasManagerData = managerValues.some((value) => String(value).trim() !== "");
      return hasManagerData && managerValues.every((value, index) => String(value || "").trim() === String(contactValues[index] || "").trim());
    },
    syncManagerContactInfo() {
      this.$set(this.branchForm, "BranchContactName", this.branchForm.BranchManagerName || "");
      this.$set(this.branchForm, "BranchContactPhone", this.branchForm.BranchManagerPhone || "");
      this.$set(this.branchForm, "BranchContactMobile", this.branchForm.BranchManagerMobile || "");
      this.$set(this.branchForm, "BranchContactEmail", this.branchForm.BranchManagerEmail || "");
    },
    toggleSameManagerContactInfo(enabled) {
      this.useSameManagerContactInfo = Boolean(enabled);
      if (this.useSameManagerContactInfo) {
        this.syncManagerContactInfo();
      }
    },
    copyBusinessBillingDefaults() {
      if (!this.branchForm) return;

      if (!this.branchForm.BranchLegalNIT && this.businessBillingNit) {
        this.$set(this.branchForm, "BranchLegalNIT", this.businessBillingNit);
      }

      if (!this.branchForm.BranchLegalNRC && this.businessBillingNrc) {
        this.$set(this.branchForm, "BranchLegalNRC", this.businessBillingNrc);
      }
    },
    async refreshBranchGeoData() {
      this.getcurrencybyCountry();

      if (this.checkcountry) {
        this.stateList = [];
        this.cityList = [];
        this.noDataLabelStates = "Seleccione un país";
        this.noDataLabelCities = "No hay ciudades disponibles";
        return;
      }

      await this.getStates();
      await this.getCities();
    },
    onCountryChange() {
      this.$set(this.branchForm, "branchdepartment", "");
      this.$set(this.branchForm, "branchmunicipality", "");
      this.stateList = [];
      this.cityList = [];
      this.getcurrencybyCountry();
      this.getStates();
    },
    onDepartmentChange() {
      this.$set(this.branchForm, "branchmunicipality", "");
      this.getCities();
    },
    async getStates() {
      if (this.checkcountry) return [];
      if (!this.branchForm?.country) return [];

      this.noDataLabelStates = "Cargando...";
      this.stateList = [];

      try {
        this.stateList = await this.$API.general.getStates(this.branchForm.country);
      } catch (error) {
        this.stateList = [];
      }

      this.noDataLabelStates = "No hay Estados disponibles";
      return this.stateList;
    },
    async getCities() {
      if (this.checkcountry) return [];
      if (!this.branchForm?.branchdepartment) return [];

      this.noDataLabelCities = "Cargando...";
      this.cityList = [];

      try {
        this.cityList = await this.$API.general.getCities(this.branchForm.branchdepartment);
      } catch (error) {
        this.cityList = [];
      }

      this.noDataLabelCities = "No hay Ciudades disponibles";
      return this.cityList;
    },
    getcurrencybyCountry() {
      const selectedCountry = (this.countryList || []).find((country) => country.name === this.branchForm.country);
      if (selectedCountry) {
        this.$set(this.branchForm, "currency", selectedCountry.currency_symbol || this.branchForm.currency || "");
      }
    },
    maskNum() {
      const fields = [
        "BranchPhone",
        "BranchMobile",
        "BranchManagerPhone",
        "BranchManagerMobile",
        "BranchContactPhone",
        "BranchContactMobile",
      ];

      fields.forEach((field) => {
        const value = String(this.branchForm?.[field] || "");
        if (!value) return;
        this.$set(this.branchForm, field, value.replace(/[^\d+]/g, ""));
      });
    },
    openCopyPasswordsDialog() {
      this.copyPasswordsSourceBranchId = this.otherBranchOptions[0]?.ID_Branch || null;
      this.copyPasswordsDialog = true;
    },
    closeCopyPasswordsDialog() {
      this.copyPasswordsDialog = false;
      this.copyPasswordsSourceBranchId = null;
    },
    async copyPasswordsFromBranch() {
      if (!this.copyPasswordsSourceBranchId) return;

      this.copyingPasswordsFromBranch = true;
      try {
        const response = await this.$API.branches.getInfoMh({
          ID_Business: this.businessId,
          ID_Branch: this.copyPasswordsSourceBranchId,
        });

        const selected = response?.select || {};
        this.authfedata.AuthFePassword = selected.AuthFePassword || "";
        this.authfedata.AuthFePasswordPri = selected.AuthFePasswordPri || "";
        this.authValidation = { status: "idle", message: "" };
        this.closeCopyPasswordsDialog();
        this.showMessage("success", "Contraseñas copiadas", "Las contraseñas se copiaron desde la sucursal seleccionada.");
      } catch (error) {
        const message = error?.response?.data?.message || "No fue posible copiar las contraseñas desde la otra sucursal.";
        this.showMessage("error", "Error", message);
      } finally {
        this.copyingPasswordsFromBranch = false;
      }
    },
    async loadBranchAuthInfo() {
      if (!this.businessId || !this.selectedBranchId) return;

      try {
        const response = await this.$API.branches.getInfoMh({
          ID_Business: this.businessId,
          ID_Branch: this.selectedBranchId,
        });

        const selected = response?.select || {};
        this.loadedBranchAuth = true;
        const resolvedNit = this.isValidNitCandidate(selected.AuthFeUser)
          ? selected.AuthFeUser
          : this.getBusinessNit() || "";
        this.authfedata.AuthFeUser = resolvedNit;
        this.authfedata.AuthFePassword = selected.AuthFePassword || "";
        this.authfedata.AuthFePasswordPri = selected.AuthFePasswordPri || "";
        this.authfedata.AuthFeActive = Number(selected.AuthFeActive ?? 1);
        this.authfedata.AuthFeMode = String(this.environment === "01" ? 1 : 0);
        this.authfedata.UrlFirmador = selected.UrlFirmador || this.presetFirmadorUrl;
        this.firmadorUrlEditable = false;

        if (!this.nitTouched) {
          this.nitValue = resolvedNit || this.nitValue;
        }
      } catch (error) {
        this.loadedBranchAuth = false;
        this.authfedata.AuthFeUser = this.getBusinessNit() || "";
        this.authfedata.AuthFePassword = "";
        this.authfedata.AuthFePasswordPri = "";
        this.authfedata.AuthFeActive = 1;
        this.authfedata.AuthFeMode = String(this.environment === "01" ? 1 : 0);
        this.authfedata.UrlFirmador = this.presetFirmadorUrl;
        this.firmadorUrlEditable = false;
        if (!this.nitTouched) {
          this.nitValue = this.getBusinessNit() || this.nitValue;
        }
      }
    },
    resolveBusinessId() {
      return (
        this.win?.meta?.business?.ID_Business ||
        this.business?.ID_Business ||
        this.currentBranchStore?.ID_Business ||
        this.currentUser?.businessid ||
        this.currentUser?.ID_Business ||
        null
      );
    },
    async refreshCurrentCertificate() {
      if (!this.businessId) return;

      try {
        const response = await this.$API.businessCertificatesMh.getActiveCertificate({
          ID_Business: this.businessId,
          environment: this.environment,
        });
        this.currentCertificate = response?.certificate || null;
      } catch (error) {
        this.currentCertificate = null;
      }
    },
    async requestBranchChange(newBranchId) {
      if (String(newBranchId) === String(this.confirmedBranchId)) return;

      this.pendingBranchId = newBranchId;
      this.branchChangeDialog = true;
      this.selectedBranchId = this.confirmedBranchId;
    },
    cancelBranchChange() {
      this.branchChangeDialog = false;
      this.pendingBranchId = null;
      this.selectedBranchId = this.confirmedBranchId;
    },
    async confirmBranchChange() {
      this.branchChangeDialog = false;
      this.selectedBranchId = this.pendingBranchId;
      this.confirmedBranchId = this.pendingBranchId;
      this.pendingBranchId = null;
      this.nitTouched = false;
      this.clearProcessingTimer();
      this.processingSeconds = 0;
      this.selectedCertificateFile = null;
      this.authValidation = { status: "idle", message: "" };
      this.step = 1;
      await this.refreshBranchSetupForm();
      await this.loadBranchAuthInfo();
      await this.refreshCurrentCertificate();
    },
    getFirmadorUrlForEnvironment(environment) {
      const urlFirmador = {
        0: "https://firmador2prueba.innovatebusiness.net/firmardocumento/",
        1: "https://firmador2prod.innovatebusiness.net/firmardocumento/",
        "00": "https://firmador2prueba.innovatebusiness.net/firmardocumento/",
        "01": "https://firmador2prod.innovatebusiness.net/firmardocumento/",
      };

      const key = String(environment ?? this.environment);
      return urlFirmador?.[key] || urlFirmador?.[key.slice(-1)] || urlFirmador?.["00"];
    },
    changeFirmadorUrl(e) {
      try {
        this.authfedata.UrlFirmador = this.getFirmadorUrlForEnvironment(e);
        this.firmadorUrlEditable = false;

        if (this.$refs.urlfirm && typeof this.$refs.urlfirm.blur === "function") {
          this.$refs.urlfirm.blur();
        }
      } catch (error) {
        console.log(error);
      }
    },
    toggleFirmadorUrlEditMode() {
      this.firmadorUrlEditable = !this.firmadorUrlEditable;

      if (this.firmadorUrlEditable) {
        this.$nextTick(() => {
          if (this.$refs.urlfirm && typeof this.$refs.urlfirm.focus === "function") {
            this.$refs.urlfirm.focus();
          }
        });
        return;
      }

      if (!this.authfedata.UrlFirmador) {
        this.authfedata.UrlFirmador = this.presetFirmadorUrl;
      }
    },
    handleEnvironmentChange() {
      this.clearProcessingTimer();
      this.processingSeconds = 0;
      this.selectedCertificateFile = null;
      this.authValidation = { status: "idle", message: "" };
      this.authfedata.AuthFeMode = String(this.environment === "01" ? 1 : 0);
      this.changeFirmadorUrl(this.environment);
      if (!this.isTestEnvironment && this.step === 3) {
        this.step = 2;
      }
      if (this.isTestEnvironment && !this.selectedBranchMhEnabled && this.step === 3) {
        this.step = 1;
      }
      this.refreshCurrentCertificate();
    },
    generatePassword(field) {
      const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%*-_";
      const buffer = new Uint32Array(24);
      window.crypto.getRandomValues(buffer);
      const password = Array.from(buffer, (value) => charset[value % charset.length]).join("");
      this.$set(this.authfedata, field, password);
    },
    async copyToClipboard(text) {
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    },
    async savePasswords() {
      if (!this.canSavePasswords) return;

      this.savingPasswords = true;
      try {
        const payload = {
          ID_Business: this.businessId,
          ID_Branch: this.selectedBranchId,
          AuthFeUser: this.nitValue,
          AuthFePassword: this.authfedata.AuthFePassword,
          AuthFePasswordPri: this.authfedata.AuthFePasswordPri,
          AuthFeActive: 1,
          AuthFeMode: String(this.environment === "01" ? 1 : 0),
          UrlFirmador: this.firmadorUrl,
        };

        const response = await this.$API.branches.updateInfoMh(payload);
        if (response?.token) {
          this.authValidation = { status: "idle", message: "" };
          this.firmadorUrlEditable = false;
          this.showMessage("success", "Guardado correcto", response?.message || "Las contraseñas MH se guardaron correctamente.");
          await this.refreshCurrentCertificate();
        } else {
          this.authValidation = { status: "idle", message: "" };
          this.showMessage(
            "error",
            "Validación fallida",
              response?.message ||
              "No fue posible validar la autenticación. Revise que el NIT, las contraseñas, el certificado y el entorno correspondan a la configuración realizada en el portal del Ministerio de Hacienda."
          );
        }
      } catch (error) {
        this.authValidation = { status: "idle", message: "" };
        this.showMessage("error", "Error", "No fue posible guardar las contraseñas de MH.");
      } finally {
        this.savingPasswords = false;
      }
    },
    handleCertificateSelection(file) {
      if (!file) {
        this.selectedCertificateFile = null;
        return;
      }

      const filename = file?.name || file?.originalname || "";
      if (!String(filename).toLowerCase().endsWith(".crt")) {
        this.selectedCertificateFile = null;
        this.showMessage("error", "Archivo inválido", "Solo se aceptan certificados .crt.");
      }
    },
    async uploadCertificate() {
      if (!this.selectedCertificateFile || this.currentCertificate) return;

      const file = this.selectedCertificateFile;
      const filename = file?.name || "";
      if (!String(filename).toLowerCase().endsWith(".crt")) {
        this.selectedCertificateFile = null;
        return;
      }

      this.uploadingCertificate = true;
      try {
        const response = await this.$API.businessCertificatesMh.uploadCertificate(file, {
          ID_Business: this.businessId,
          environment: this.environment,
          AuthFeUser: this.nitValue,
          created_by: this.currentUser?.id || null,
        });

        this.currentCertificate = response?.certificate || null;
        this.selectedCertificateFile = null;
        this.authValidation = { status: "idle", message: "" };
        this.step = 1;
        this.startProcessingTimer();
      } catch (error) {
        const message = error?.response?.data?.message || "No fue posible subir el certificado.";
        this.showMessage("error", "Error", message);
      } finally {
        this.uploadingCertificate = false;
      }
    },
    startProcessingTimer() {
      this.clearProcessingTimer();
      this.processingSeconds = 60;
      this.step = 1;

      this.processingTimer = window.setInterval(async () => {
        if (this.processingSeconds > 0) {
          this.processingSeconds -= 1;
        }

        if (this.processingSeconds <= 0) {
          this.clearProcessingTimer();
          await this.validateAuthentication();
        }
      }, 1000);
    },
    clearProcessingTimer() {
      if (this.processingTimer) {
        window.clearInterval(this.processingTimer);
        this.processingTimer = null;
      }
    },
    openDeleteDialog() {
      this.deleteConfirmChecked = false;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteConfirmChecked = false;
    },
    async deleteCertificate() {
      if (!this.deleteConfirmChecked || !this.currentCertificate) return;

      this.deletingCertificate = true;
      try {
        this.clearProcessingTimer();
        this.processingSeconds = 0;
        const response = await this.$API.businessCertificatesMh.deleteCertificate({
          id: this.currentCertificate.id,
          ID_Business: this.businessId,
          environment: this.environment,
          deleted_by: this.currentUser?.id || null,
        });

        this.currentCertificate = null;
        this.selectedCertificateFile = null;
        this.authValidation = { status: "idle", message: "" };
        this.closeDeleteDialog();
        this.showMessage("success", "Certificado eliminado", response?.message || "El certificado fue eliminado correctamente.");
        this.step = 1;
      } catch (error) {
        const message = error?.response?.data?.message || "No fue posible eliminar el certificado.";
        this.showMessage("error", "Error", message);
      } finally {
        this.deletingCertificate = false;
      }
    },
    async validateAuthentication() {
      if (this.processingSeconds > 0) return;
      if (!this.canSavePasswords) return;

      this.validatingAuth = true;
      try {
        const response = await this.$API.branches.updateInfoMh({
          ID_Business: this.businessId,
          ID_Branch: this.selectedBranchId,
          AuthFeUser: this.nitValue,
          AuthFePassword: this.authfedata.AuthFePassword,
          AuthFePasswordPri: this.authfedata.AuthFePasswordPri,
          AuthFeActive: 1,
          AuthFeMode: String(this.environment === "01" ? 1 : 0),
          UrlFirmador: this.firmadorUrl,
        });

        if (response?.token) {
          this.authValidation = {
            status: "success",
            message: response?.message || "Configuración validada correctamente.",
          };
          return;
        }

        this.authValidation = {
          status: "error",
          message:
            response?.message ||
            "No fue posible validar la autenticación. Revise que el NIT, las contraseñas, el certificado y el entorno correspondan a la configuración realizada en el portal del Ministerio de Hacienda.",
        };
      } catch (error) {
        this.authValidation = {
          status: "error",
          message:
            error?.response?.data?.message ||
            "No fue posible validar la autenticación. Revise que el NIT, las contraseñas, el certificado y el entorno correspondan a la configuración realizada en el portal del Ministerio de Hacienda.",
        };
      } finally {
        this.validatingAuth = false;
      }
    },
    handleBranchLogoSelection(file) {
      if (!file) {
        this.branchLogoFile = null;
        this.branchLogoPreview = this.branchForm?.BranchLogoPath || null;
        return;
      }

      this.branchLogoFile = file;
      if (this.branchLogoPreview && this.branchLogoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(this.branchLogoPreview);
      }
      this.branchLogoPreview = URL.createObjectURL(file);
    },
    async saveBranchData() {
      if (!this.branchForm?.ID_Branch) {
        this.showMessage("error", "Sucursal no disponible", "Seleccione una sucursal válida antes de guardar.");
        return;
      }

      this.savingBranchData = true;
      try {
        if (this.useSameManagerContactInfo) {
          this.syncManagerContactInfo();
        }

        const bccEmails = Array.isArray(this.branchForm.BccEmails)
          ? this.branchForm.BccEmails.map((email) => String(email || "").trim()).filter(Boolean).join(", ")
          : String(this.branchForm.BccEmails || "").trim();

        const payload = {
          ...this.branchForm,
          ID_Business: this.businessId,
          ID_User_Update: String(this.currentUser?.id || this.currentUser?.ID_User || this.currentUser?.id_user || this.currentUser?.id_user_login || ""),
          BccEmails: bccEmails,
        };

        const formdata = new FormData();
        formdata.append("branch", JSON.stringify(payload));
        if (this.branchLogoFile) {
          formdata.append("file", this.branchLogoFile);
        }

        const response = await this.$API.branches.updateBranch(formdata);
        if (response?.operation) {
          const index = this.branches.findIndex((item) => String(item.ID_Branch) === String(this.branchForm.ID_Branch));
          if (index >= 0) {
            this.$set(this.branches, index, {
              ...this.branches[index],
              ...this.branchForm,
              BranchLogoPath: response?.data?.BranchLogoPath || this.branchLogoPreview || this.branchForm.BranchLogoPath,
            });
          }

          const savedBranch = {
            ...this.branchForm,
            BccEmails: bccEmails,
            BranchLogoPath: response?.data?.BranchLogoPath || this.branchLogoPreview || this.branchForm.BranchLogoPath,
          };

          this.branchForm = this.buildBranchForm(savedBranch);
          this.branchLogoPreview = savedBranch.BranchLogoPath || null;
          this.selectedBranchId = this.branchForm.ID_Branch;
          this.confirmedBranchId = this.branchForm.ID_Branch;
          this.$store.commit("setBranch", savedBranch);
          this.useSameManagerContactInfo = this.areManagerAndContactEqual();
          await this.loadBranches({ preserveSelection: true });
          this.showMessage("success", "Sucursal actualizada", response?.message || "La información de la sucursal se guardó correctamente.");
        } else {
          this.showMessage("error", "Error", "No fue posible guardar la información de la sucursal.");
        }
      } catch (error) {
        const message = error?.response?.data?.message || "No fue posible guardar la información de la sucursal.";
        this.showMessage("error", "Error", message);
      } finally {
        this.savingBranchData = false;
      }
    },
    showMessage(type, title, body) {
      if (this.$toasted) {
        this.$toasted.show(body, {
          type,
          duration: 4000,
          position: "top-right",
        });
        return;
      }

      this.$emit("notify", { type, title, body });
    },
  },
};
</script>

<style scoped>
.business-initial-setup {
  min-height: calc(100vh - 120px);
  padding: 16px;
  background:
    radial-gradient(circle at top left, rgba(25, 118, 210, 0.16), transparent 30%),
    radial-gradient(circle at top right, rgba(0, 161, 120, 0.12), transparent 28%),
    linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%);
}

.business-initial-setup--fullscreen {
  position: fixed;
  inset: 0;
  z-index: 9998;
  padding: 0;
  background:
    radial-gradient(circle at top left, rgba(25, 118, 210, 0.12), transparent 28%),
    radial-gradient(circle at top right, rgba(0, 161, 120, 0.1), transparent 26%),
    linear-gradient(180deg, #eef4fb 0%, #e8f0f8 100%);
}

.setup-shell {
  min-height: calc(100vh - 152px);
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(16, 24, 40, 0.08);
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.08);
}

.business-initial-setup--fullscreen .setup-shell {
  min-height: 100vh;
  height: 100vh;
  width: 100vw;
  border-radius: 0;
  border-left: 0;
  border-right: 0;
  box-shadow: none;
}

.business-initial-setup--fullscreen .setup-body {
  padding: 20px 22px 22px;
}

.setup-header {
  position: relative;
  background: linear-gradient(135deg, #071421 0%, #12304d 52%, #0f5f73 100%);
  color: #ffffff !important;
  padding: 24px 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.setup-header.v-card__title {
  color: #ffffff !important;
}

.setup-header.v-card__title *,
.setup-header.v-card__title *::before,
.setup-header.v-card__title *::after {
  color: inherit !important;
}

.setup-header__content {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.setup-header__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.setup-timer-chip {
  font-size: 11px;
  letter-spacing: 0.02em;
  height: 22px;
  max-width: fit-content;
}

.setup-kicker {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 12px;
  opacity: 0.98;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.92) !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.setup-header h2 {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  color: #ffffff !important;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.38);
}

.setup-header p {
  margin: 10px 0 0;
  max-width: 760px;
  opacity: 1;
  color: rgba(255, 255, 255, 0.92) !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.setup-close {
  align-self: flex-start;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 999px;
}

.setup-action {
  align-self: flex-start;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 999px;
}

.setup-close .v-icon {
  color: #ffffff !important;
}

.setup-action .v-icon {
  color: #ffffff !important;
}

.setup-body {
  padding: 24px;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.setup-sidebar {
  position: sticky;
  top: 16px;
  align-self: flex-start;
}

.summary-card {
  border-radius: 18px;
  padding: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f3f7fb 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.summary-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #567;
  margin-bottom: 8px;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #102a43;
}

.summary-note {
  margin-top: 10px;
  font-size: 13px;
  color: #425466;
}

.setup-stepper-rail {
  background: transparent;
}

.setup-stepper-rail .v-stepper__step {
  cursor: pointer;
}

.setup-stepper {
  background: transparent;
  box-shadow: none;
}

.panel-subtitle {
  font-size: 18px;
  font-weight: 700;
  color: #102a43;
  margin: 8px 0 12px;
}

.setup-panel {
  padding: 6px 2px 12px;
  background: transparent;
}

.setup-panel--white {
  background: #ffffff;
  border-radius: 20px;
  padding: 18px 18px 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.panel-title {
  font-size: 22px;
  font-weight: 700;
  color: #102a43;
  margin-bottom: 8px;
}

.panel-text {
  color: #455468;
  margin-bottom: 18px;
  max-width: 900px;
}

.password-section {
  margin-top: 4px;
  padding: 18px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(248, 251, 255, 0.96) 0%, rgba(241, 247, 253, 0.96) 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 18px;
}

.panel-actions--split {
  justify-content: space-between;
  align-items: center;
}

.password-card {
  height: 100%;
  border-radius: 18px;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.password-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #102a43;
  padding: 0 0 8px;
}

.password-card p {
  color: #455468;
  font-size: 13px;
  min-height: 100px;
}

.password-card .v-card__text {
  padding-top: 0;
}

.password-card .v-card__title {
  padding-bottom: 8px;
}

.setup-summary-card {
  border-radius: 18px;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.branch-logo-box {
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff, #f5f8fd);
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 16px;
  margin-bottom: 14px;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.password-actions--top {
  margin-bottom: 12px;
}

.firmador-url-control {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
}

.firmador-url-control .v-text-field {
  flex: 1 1 320px;
}

.firmador-url-control__button {
  flex: 0 0 auto;
}

.processing-box {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 22px;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff, #f5f8fd);
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.processing-block {
  margin-top: 24px;
}

.processing-box__timer {
  display: flex;
  flex-direction: column;
}

.timer-value {
  font-size: 56px;
  line-height: 1;
  font-weight: 800;
  color: #102a43;
}

.timer-label {
  font-size: 14px;
  color: #52667a;
  margin-top: 6px;
}

.processing-box__done {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
}

.result-list {
  background: white;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.delete-dialog__title {
  background: #c62828;
  color: #ffffff !important;
}

.delete-dialog__alert {
  border-radius: 12px;
}

.final-state-card {
  border-radius: 18px;
  padding: 18px;
  background: white;
}

.final-state-card__title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
}

.final-state-card__line {
  font-size: 13px;
  color: #425466;
  word-break: break-word;
}

code {
  background: rgba(16, 42, 67, 0.06);
  padding: 2px 6px;
  border-radius: 6px;
}

@media (max-width: 1264px) {
  .setup-sidebar {
    position: static;
  }
}

@media (max-width: 960px) {
  .setup-shell {
    min-height: auto;
  }

  .setup-body {
    padding: 16px;
  }

  .setup-header {
    padding: 18px;
  }

  .setup-header__content {
    flex-direction: column;
  }

  .setup-header__actions {
    align-self: flex-end;
  }

  .setup-header h2 {
    font-size: 22px;
  }

  .panel-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .panel-actions .v-btn {
    width: 100%;
  }

  .processing-box {
    flex-direction: column;
    align-items: flex-start;
  }

  .timer-value {
    font-size: 42px;
  }

  .branch-logo-box {
    min-height: 180px;
  }
}
</style>
